import { InvitationPayload, useAcceptWorkspaceInvitation, useRevokeWorkspaceInvitation } from 'dataQueries'
import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { EntityKeys, QueryType } from 'utils/reactQuery'

import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { Path } from 'constants/router'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { pollUserMeUntilWorkspaceMember } from 'utils/helpers/pollUserMeUntilWorkspaceMember'
import { useRememberWorkspace } from './useRememberWorkspace'
import { logger } from 'utils/logging'

export const useWorkspaceInvitationActions = () => {
  const { t } = useTranslation(['workspace_profile'])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()


  const revokeInvitation = useRevokeWorkspaceInvitation()
  const acceptInvitation = useAcceptWorkspaceInvitation()
  const rememberWorkspace = useRememberWorkspace()

  const handleDecline = useCallback(async (payload: InvitationPayload) => {
    try {
      await revokeInvitation.mutateAsync(payload)
      spawnSuccessToast(t('invitation_declined'))
      queryClient.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })
      await queryClient.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    } catch (error) {
      logger.error('Failed to decline workspace invitation', error, { context: { component: 'useWorkspaceInvitationActions', action: 'handleDecline' } })
      spawnErrorToast(t('invitation_decline_error'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutateAsync is stable
  }, [revokeInvitation.mutateAsync, spawnSuccessToast, spawnErrorToast, t, queryClient])

  const handleAccept = useCallback(async (payload: InvitationPayload) => {
    try {
      await acceptInvitation.mutateAsync(payload)
      spawnSuccessToast(t('invitation_accepted'))

      logAnalyticsEvent(AnalyticsEvent.ACCEPT_INVITATION, { workspaceId: payload.workspaceId })
      // Persist the accepted workspace BEFORE invalidating userMe. UserDataContext resolves the
      // active workspace from the stored id as soon as the userMe refetch lands, so the id must
      // already be in storage by then. Writing it after the awaited invalidation (previous
      // behavior) let resolution run against the old id, leaving the switcher on the previous
      // workspace (QA-258).
      sessionStorage.setItem('workspaceId', payload.workspaceId)
      queryClient.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })
      await queryClient.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
      // Redirect into the accepted workspace so the user lands in it and the switcher reflects it (ISSUE-2073).
      navigate(Path.INDEX)
      // The invalidation above fires exactly one userMe refetch. If it lands before the backend
      // propagates the new membership, UserDataContext's PENDING guard keeps the previous
      // workspace selected and nothing would refetch userMe again — so keep refetching (bounded)
      // until the accepted workspace resolves as a member (QA-258).
      //
      // Remembering the workspace is sequenced after that poll: the preference write needs a live
      // membership server-side, so issuing it inside the same propagation window can have the lookup
      // miss and answer 403 — which is swallowed, silently losing the choice. Attempted via `finally`
      // rather than on success only, so an exhausted poll still tries instead of dropping it.
      void pollUserMeUntilWorkspaceMember(queryClient, payload.workspaceId)
        .finally(() => rememberWorkspace(payload.workspaceId))
    } catch (error) {
      logger.error('Failed to accept workspace invitation', error, { context: { component: 'useWorkspaceInvitationActions', action: 'handleAccept' } })
      spawnErrorToast(t('invitation_accept_error'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutateAsync is stable
  }, [acceptInvitation.mutateAsync, spawnSuccessToast, spawnErrorToast, t, queryClient, navigate, rememberWorkspace])

  const isDeclinePending = useCallback((invitationId: string) =>
    revokeInvitation.isPending && revokeInvitation.variables?.invitationId === invitationId
    , [revokeInvitation.isPending, revokeInvitation.variables?.invitationId])

  const isAcceptPending = useCallback((invitationId: string) =>
    acceptInvitation.isPending && acceptInvitation.variables?.invitationId === invitationId
    , [acceptInvitation.isPending, acceptInvitation.variables?.invitationId])

  return { handleDecline, handleAccept, isDeclinePending, isAcceptPending }
}
